import React from 'react'
import {
  Button,
  Popover,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Divider,
  Tooltip,
  Badge,
  IconButton,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

import StyledButton from 'views/AssetsReports/components/StyledButton/StyledButton'
import useMultiSelectFilterLogic from './MultiSelectFilter.logic'
import { MultiSelectFilterProps } from './MultiSelectFilter.types'
import styles from './MultiSelectFilter.module.scss'

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = (props) => {
  const { label, compact = false, disabled = false } = props

  const {
    id,
    open,
    anchorEl,
    selected,
    searchTerm,
    filteredOptions,
    popoverPosition,
    handleApply,
    handleClick,
    handleClose,
    handleReset,
    setSearchTerm,
    handleSelectAll,
    handleToggleValue,
    handleSelectNone,
  } = useMultiSelectFilterLogic(props)

  if (compact) {
    return (
      <>
        <Badge
          badgeContent={selected.length}
          color="primary"
          className={styles.badge}
        >
          <IconButton
            aria-describedby={id}
            onClick={handleClick}
            color="primary"
            aria-label={`Filter by ${label}`}
            size="medium"
            disabled={disabled}
            className={`${styles.compactIconButton} ${
              selected.length > 0 ? styles.selected : ''
            }`}
          >
            <FilterListIcon />
          </IconButton>
        </Badge>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={popoverPosition.anchorOrigin}
          transformOrigin={popoverPosition.transformOrigin}
          marginThreshold={16}
          PaperProps={{
            elevation: 8,
            className: styles.popoverPaper,
          }}
        >
          <Box className={styles.popoverHeader}>
            <Box className={styles.headerTop}>
              <Typography variant="h6" className={styles.headerTitle}>
                {label}
              </Typography>
              <Typography variant="body2" className={styles.selectedCount}>
                {selected.length} selected
              </Typography>
            </Box>

            <Box className={styles.searchContainer}>
              <TextField
                placeholder={`Search ${label.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
                className={styles.searchField}
                InputProps={{
                  startAdornment: (
                    <SearchIcon color="action" className={styles.searchIcon} />
                  ),
                }}
              />
            </Box>

            <Box className={styles.actionButtons}>
              <Button
                size="small"
                onClick={handleSelectAll}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'var(--primary)',
                  '&:hover': {
                    backgroundColor:
                      'color-mix(in srgb, var(--primary) 8%, transparent)',
                  },
                }}
                className={styles.selectAllButton}
              >
                Select All
              </Button>
              <Button
                size="small"
                onClick={handleSelectNone}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'var(--grayscale-dark)',
                  '&:hover': {
                    backgroundColor:
                      'color-mix(in srgb, var(--grayscale-dark) 8%, transparent)',
                  },
                }}
              >
                Select None
              </Button>
              <Button
                size="small"
                onClick={handleReset}
                startIcon={<RestartAltIcon fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: 'var(--grayscale-dark)',
                  '&:hover': {
                    backgroundColor:
                      'color-mix(in srgb, var(--grayscale-dark) 8%, transparent)',
                  },
                }}
              >
                Default
              </Button>
            </Box>
          </Box>

          <Box className={styles.optionsContainer}>
            {filteredOptions.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                className={styles.noOptionsText}
              >
                No options match your search
              </Typography>
            ) : (
              filteredOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={selected.includes(option)}
                      onChange={() => handleToggleValue(option)}
                      color="primary"
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      className={styles.optionCheckbox}
                    />
                  }
                  label={
                    <Typography variant="body2" className={styles.optionLabel}>
                      {option}
                    </Typography>
                  }
                  className={styles.optionItem}
                />
              ))
            )}
          </Box>

          <Divider />

          <Box className={styles.popoverFooter}>
            <Button
              onClick={handleApply}
              variant="contained"
              color="primary"
              className={styles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Popover>
      </>
    )
  }

  return (
    <>
      <Tooltip
        title={
          disabled
            ? `No ${label.toLowerCase()} data available`
            : `Filter by ${label}`
        }
      >
        <Badge
          badgeContent={selected.length}
          color="primary"
          className={styles.badge}
        >
          <StyledButton
            size="small"
            aria-describedby={id}
            variant="contained"
            color="primary"
            disabled={disabled}
            startIcon={<FilterListIcon />}
            label={label}
            onClick={handleClick}
          />
        </Badge>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={popoverPosition.anchorOrigin}
        transformOrigin={popoverPosition.transformOrigin}
        marginThreshold={16}
        PaperProps={{
          elevation: 8,
          className: styles.popoverPaper,
        }}
      >
        <Box className={styles.popoverHeader}>
          <Box className={styles.headerTop}>
            <Typography variant="h6" className={styles.headerTitle}>
              {label}
            </Typography>
            <Typography variant="body2" className={styles.selectedCount}>
              {selected.length} selected
            </Typography>
          </Box>

          <Box className={styles.searchContainer}>
            <TextField
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              className={styles.searchField}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" className={styles.searchIcon} />
                ),
              }}
            />
          </Box>

          <Box className={styles.actionButtons}>
            <Button
              size="small"
              onClick={handleSelectAll}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'var(--primary)',
                '&:hover': {
                  backgroundColor:
                    'color-mix(in srgb, var(--primary) 8%, transparent)',
                },
              }}
              className={styles.selectAllButton}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={handleSelectNone}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'var(--grayscale-dark)',
                '&:hover': {
                  backgroundColor:
                    'color-mix(in srgb, var(--grayscale-dark) 8%, transparent)',
                },
              }}
            >
              Select None
            </Button>
            <Button
              size="small"
              onClick={handleReset}
              startIcon={<RestartAltIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: 'var(--grayscale-dark)',
                '&:hover': {
                  backgroundColor:
                    'color-mix(in srgb, var(--grayscale-dark) 8%, transparent)',
                },
              }}
            >
              Default
            </Button>
          </Box>
        </Box>

        <Box className={styles.optionsContainer}>
          {filteredOptions.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              className={styles.noOptionsText}
            >
              No options match your search
            </Typography>
          ) : (
            filteredOptions.map((option) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={selected.includes(option)}
                    onChange={() => handleToggleValue(option)}
                    color="primary"
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    className={styles.optionCheckbox}
                  />
                }
                label={
                  <Typography variant="body2" className={styles.optionLabel}>
                    {option}
                  </Typography>
                }
                className={styles.optionItem}
              />
            ))
          )}
        </Box>

        <Divider />

        <Box className={styles.popoverFooter}>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            className={styles.applyButton}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default MultiSelectFilter
